import {
  CountryCode,
  isPossiblePhoneNumber,
  getCountryCallingCode,
  parsePhoneNumberWithError,
} from 'libphonenumber-js';
import { formatNumberAsYouType } from '@electricjs/arc';
import { Employee } from '@/types/employees';

type FormatPhoneNumberProps = Employee['phone'] | null;

export const formatPhoneNumber = (
  phoneNumber: FormatPhoneNumberProps
): string => {
  if (!phoneNumber || phoneNumber.length !== 10 || isNaN(Number(phoneNumber))) {
    return '';
  }

  const areaCode = phoneNumber.slice(0, 3);
  const centralOfficeCode = phoneNumber.slice(3, 6);
  const lineNumber = phoneNumber.slice(6, 10);

  return `(${areaCode}) ${centralOfficeCode}-${lineNumber}`;
};

export const formatQRCodeReceiverSitePhoneNumber = (
  phoneNumber: string,
  allowedCountries: string[] = ['US']
): string => {
  try {
    const parsed = parsePhoneNumberWithError(phoneNumber);

    if (!allowedCountries.includes(parsed.country ?? '')) {
      return '';
    }

    // Returns the phone number in the format (###) ###-####
    return parsed.formatNational();
  } catch {
    // If we can't parse the phone number, assume it's from the US.
    // This may happen since we have phone numbers stored in the database w/o the leading "+" and country code.
    return formatPhoneNumber(phoneNumber);
  }
};

export const formatInternationalPhoneNumber = (
  phoneNumber: string | null | undefined,
  fallbackCountry: string | undefined = 'US'
): string => {
  if (!phoneNumber) {
    return '';
  }

  try {
    // Try to parse the phone number to detect its actual country
    const parsed = parsePhoneNumberWithError(
      phoneNumber,
      fallbackCountry as CountryCode
    );

    // Use the detected country or fallback to the provided country
    const actualCountry = (parsed.country || fallbackCountry) as CountryCode;

    if (isPossiblePhoneNumber(phoneNumber, actualCountry)) {
      const countryCode = getCountryCallingCode(actualCountry);
      const nationalFormatted = formatNumberAsYouType(
        phoneNumber,
        actualCountry
      );
      return `+${countryCode} ${nationalFormatted}`;
    }
    return '';
  } catch {
    return '';
  }
};
